import React, { useState, useEffect } from 'react';
import { getBarrioById, createBarrio, updateBarrio } from '../services/barrioService';
import { getCiudades } from '../services/ciudadService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, MapPin, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function BarrioForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        ciudadId: ''
    });

    const [ciudades, setCiudades] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);

    useEffect(() => {
        cargarCiudades();
        if (isEditMode) {
            cargarBarrio();
        }
    }, [id]);

    const cargarCiudades = async () => {
        try {
            const data = await getCiudades();
            setCiudades(data);
        } catch (error) {
            toast.error('Error al cargar ciudades');
        }
    };

    const cargarBarrio = async () => {
        try {
            const data = await getBarrioById(id);
            setFormData({
                nombre: data.nombre,
                ciudadId: data.ciudadId || ''
            });
        } catch (error) {
            toast.error('Error al cargar barrio');
            navigate('/parametrizacion/barrios');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!formData.ciudadId) {
                toast.error('Debe seleccionar una ciudad');
                return;
            }

            setLoading(true);
            if (isEditMode) {
                await updateBarrio(id, formData);
                toast.success('Barrio actualizado');
            } else {
                await createBarrio(formData);
                toast.success('Barrio creado con éxito');
            }
            setTimeout(() => navigate('/parametrizacion/barrios'), 1000);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <div className="p-20 text-center uppercase font-black text-slate-400 tracking-[0.2em]">Cargando...</div>;
    }

    return (
        <div className="max-w-2xl mx-auto bg-white shadow-xl border border-slate-200 mb-10 overflow-hidden font-sans">
            <Toaster position="top-right" />
            <div className="bg-slate-700 p-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/parametrizacion/barrios')} className="p-2 hover:bg-slate-600 rounded-full transition-all" type="button">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-bold uppercase tracking-tight">
                        {isEditMode ? 'Editar Barrio' : 'Nuevo Barrio'}
                    </h1>
                </div>
                <MapPin className="w-10 h-10 text-emerald-500 opacity-30" />
            </div>

            <form onSubmit={handleSubmit} className="p-10 space-y-8">
                <div className="space-y-6">
                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Nombre del Barrio</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase text-slate-800 focus:border-emerald-500 outline-none"
                            value={formData.nombre}
                            onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Ciudad</label>
                        <select
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase focus:border-emerald-500 outline-none"
                            value={formData.ciudadId}
                            onChange={(e) => setFormData({ ...formData, ciudadId: e.target.value })}
                        >
                            <option value="">Seleccione Ciudad</option>
                            {ciudades.map(c => (
                                <option key={c.id} value={c.id}>{c.nombre}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="pt-8 border-t flex justify-end gap-4">
                    <button type="button" onClick={() => navigate('/parametrizacion/barrios')} className="text-xs font-black uppercase text-slate-400 hover:text-slate-600">DESCARTAR</button>
                    <button type="submit" disabled={loading} className="px-10 py-3 bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-500" />}
                        {isEditMode ? 'ACTUALIZAR' : 'GUARDAR REGISTRO'}
                    </button>
                </div>
            </form>
        </div>
    );
}